import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { loaderRevsApi } from '@/api/loaders';
import type { ListLoaderRevsResponse } from '@bhops/shared';
import { Tooltip } from '@/components/Tooltip';
import { kstYmd } from '@/lib/format-date';

// docs/plan/loader-config-code-hover-2026-09 — 구성코드(E01·E03…)에 마우스를 올리면
// 각 코드가 **어떤 개조인지** 보이게 한다.
//
// 왜 화면이 이름을 붙이나(서버 무변경):
//   구성코드는 서버가 적용 개조 집합에서 재계산해 LoaderUnit.configCode 에 저장한
//   문자열이고(loader-revs.service recomputeConfigCode → composeLoaderConfigCode),
//   목록 응답(LoaderSummaryDto)에는 그 문자열과 개조 수만 실린다 — 이름은 없다.
//   이름의 정본은 개조 관리 목록(GET /loader-revs)이다. 목록 응답에 이름을 실으면
//   /loaders 를 부르는 모든 자리(목록·재고 피커·로봇 상세)의 응답이 함께 무거워지고,
//   이 화면 변경 하나에 서버 배포가 묶인다. 화면이 개조 목록을 한 번 받아 코드→이름
//   사전을 만드는 쪽이 가볍고 배포도 화면만으로 끝난다.
//
// 왜 한 부품인가:
//   같은 문자열이 로더 목록 · 로봇 상세(로더 카드) · 로봇 상세 로더 탭 · 로더 상세
//   네 곳에 나온다. 네 곳에 각자 붙이면 한 곳만 고쳐지고 나머지가 옛 모양으로 남는다.
//
// 알아 둘 제약(실측):
//   - 말풍선(Tooltip)은 pointerEvents:'none' — 안에 링크를 넣어 개조 관리로 보낼 수 없다.
//   - 말풍선 본문은 whiteSpace:'pre-line' — 줄바꿈 문자로 여러 줄을 만든다(표 마크업 아님).
//   - 구분자는 가운뎃점 '·'(U+00B7, 공백 없음). 그대로 쪼개면 코드가 나온다.
//   - 로봇 등록 창의 로더 <option>(RobotForm)은 브라우저가 그리는 선택 상자라 감쌀 수
//     없다 — 그 자리만 예외다.

/** 구성코드 문자열의 구분자 — packages/shared/src/loader.ts composeLoaderConfigCode 와 같은 글자. */
const CONFIG_CODE_SEP = '·';

interface RevBrief {
  displayName: string;
  effectiveAt: string | null;
  active: boolean;
}

/**
 * 응답 → 코드 사전. **모듈 최상위 함수**여야 한다 — react-query 는 select 의 결과를
 * 함수 참조가 같을 때만 기억한다. 컴포넌트 안에서 만들면 목록 133행이 각자 캐시가
 * 바뀔 때마다 사전을 새로 만든다(개조 관리 화면의 무효화가 이 키 앞자락에 걸린다).
 */
function toRevIndex(res: ListLoaderRevsResponse): Map<string, RevBrief> {
  const m = new Map<string, RevBrief>();
  for (const rev of res.items) {
    m.set(rev.code, {
      displayName: rev.displayName,
      effectiveAt: rev.effectiveAt,
      active: rev.active,
    });
  }
  return m;
}

/** 한 코드의 설명 한 줄 — 사전에 없으면 지어내지 않고 없다고 적는다. */
function describeCode(rev: RevBrief | undefined): string {
  if (!rev) return '개조 관리에 없는 코드입니다';
  const parts = [rev.displayName];
  if (!rev.active) parts.push('(보관)');
  const day = rev.effectiveAt ? kstYmd(rev.effectiveAt) : null;
  if (day) parts.push(`· ${day}`);
  return parts.join(' ');
}

export function LoaderConfigCode({
  code,
  fallback,
  focusable = false,
  style,
}: {
  /** 서버가 준 구성코드 문자열(예 "E01·E03"). 없으면 fallback 만 그린다. */
  code: string | null | undefined;
  /** 구성코드가 없을 때 그릴 것. 기본은 흐린 '–'. */
  fallback?: ReactNode;
  /**
   * 감싼 요소를 탭 정지점으로 만들지. **목록은 false**(행이 133개면 탭 정지점도 133개가
   * 된다 — 로봇 목록의 출고 표시·구분 칸과 같은 관례). 화면에 하나뿐인 상세 카드는 true.
   * false 일 때 뜻은 aria-label 이 감당한다(그래서 span 에 role 을 준다 — 역할 없는
   * 요소의 aria-label 은 브라우저가 무시한다).
   */
  focusable?: boolean;
  style?: CSSProperties;
}) {
  const codes = useMemo(
    () =>
      (code ?? '')
        .split(CONFIG_CODE_SEP)
        .map((s) => s.trim())
        .filter(Boolean),
    [code],
  );

  // 쿼리 키는 로더 등록/편집 창의 피커(`['loader-revs','picker']`)와 **일부러 다르다** —
  // 그쪽은 보관을 뺀 목록이라 같은 키를 쓰면 서로의 캐시를 덮어 피커에 보관 개조가
  // 섞이거나 여기 이름이 빈다. `active=all` 이라야 옛 로더에 붙은 보관 개조도 풀린다.
  // 붙일 코드가 없으면 아예 부르지 않는다(구성코드 없는 로더 상세를 열 때의 헛걸음).
  const revsQ = useQuery({
    queryKey: ['loader-revs', 'catalog-all'],
    queryFn: () => loaderRevsApi.list('all'),
    select: toRevIndex,
    staleTime: 60_000,
    enabled: codes.length > 0,
  });

  if (codes.length === 0) {
    return <>{fallback ?? <span style={{ color: 'var(--fg-muted)' }}>–</span>}</>;
  }

  const byCode = revsQ.data;
  // 이름을 못 붙이는 세 경우는 서로 다른 사실이다 — 하나로 뭉뚱그리면 "이 개조는 등록돼
  // 있지 않다"는 **없는 주장**을 하게 된다(대시보드의 구버전 서버 안내와 같은 규칙).
  const note = revsQ.isPending
    ? '이름 불러오는 중…'
    : revsQ.isError
      ? '이름을 불러오지 못했습니다'
      : !byCode || byCode.size === 0
        ? '개조 관리 목록이 비어 있어 이름을 붙일 수 없습니다'
        : null;
  const lines = note ? [] : codes.map((c) => ({ code: c, text: describeCode(byCode?.get(c)) }));

  const content = (
    <>
      <span style={tipHead}>{`구성코드 ${codes.length}개, 이름은 개조 관리 기준`}</span>
      {note ? (
        <span>
          {'\n'}
          {note}
        </span>
      ) : (
        lines.map((l) => (
          <span key={l.code}>
            {'\n'}
            <strong style={{ fontFamily: 'var(--font-mono)' }}>{l.code}</strong>
            {` · ${l.text}`}
          </span>
        ))
      )}
    </>
  );

  // 스크린리더 몫 — 말풍선은 마우스/포커스로만 뜬다. role 없이 aria-label 만 주면
  // 브라우저가 이름을 노출하지 않으므로 role="img"(글자 덩어리 하나로 읽힌다)를 준다.
  const ariaLabel = note
    ? `구성코드 ${codes.join(', ')}. ${note}`
    : `구성코드: ${lines.map((l) => `${l.code} ${l.text}`).join(', ')}`;

  return (
    <Tooltip content={content} focusable={focusable} maxWidth={340} style={style}>
      <span role="img" aria-label={ariaLabel} style={codeText} data-testid="loader-config-code">
        {codes.join(CONFIG_CODE_SEP)}
      </span>
    </Tooltip>
  );
}

/** 올릴 수 있다는 신호 — 점선 밑줄 + 도움말 커서. 없으면 올려 볼 생각을 못 한다. */
const codeText: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  textDecoration: 'underline dotted',
  textDecorationColor: 'var(--fg-muted)',
  textUnderlineOffset: 3,
  cursor: 'help',
};

const tipHead: CSSProperties = {
  color: 'var(--fg-muted)',
  fontSize: 11,
};
